import { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "./ui/dialog";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Textarea } from "./ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "./ui/select";
import { toast } from "sonner";
import { createEnquiry } from "../lib/api";

export const BookingDialog = ({ open, onOpenChange, source = "free-trial" }) => {
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    classType: "Group Offline",
    message: "",
  });
  const [loading, setLoading] = useState(false);

  const update = (k) => (e) =>
    setForm((f) => ({ ...f, [k]: e?.target ? e.target.value : e }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.phone) {
      toast.error("Please fill in name, email and phone.");
      return;
    }
    setLoading(true);
    try {
      await createEnquiry({
        type: "booking",
        name: form.name,
        email: form.email,
        phone: form.phone,
        class_type: form.classType,
        message: form.message,
        source,
      });
      toast.success("Namaste! We've received your request — we'll reach out shortly.");
      onOpenChange(false);
      setForm({
        name: "",
        email: "",
        phone: "",
        classType: "Group Offline",
        message: "",
      });
    } catch (err) {
      const detail =
        err?.response?.data?.detail ||
        err?.message ||
        "Something went wrong. Please try again.";
      toast.error(detail);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        data-testid="booking-dialog"
        className="sm:max-w-[520px] bg-[#F9F6F0] border-[#1F4A38]/15 rounded-[24px] p-0 overflow-hidden"
      >
        <div className="bg-[#1F4A38] text-[#F9F6F0] px-8 py-7">
          <DialogHeader>
            <DialogTitle className="font-serif-display text-3xl font-light">
              Book Your Practice
            </DialogTitle>
            <DialogDescription className="text-[#EAC98A]/90 mt-1">
              Share a few details — we'll guide you from here.
            </DialogDescription>
          </DialogHeader>
        </div>
        <form onSubmit={submit} className="p-8 space-y-5">
          <div className="grid grid-cols-1 gap-5">
            <div>
              <Label className="text-[#153326]">Full Name *</Label>
              <Input
                data-testid="booking-name"
                value={form.name}
                onChange={update("name")}
                placeholder="Your name"
                className="mt-1.5 bg-white border-[#1F4A38]/15 rounded-xl h-11"
              />
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <Label className="text-[#153326]">Email *</Label>
                <Input
                  data-testid="booking-email"
                  type="email"
                  value={form.email}
                  onChange={update("email")}
                  placeholder="you@email.com"
                  className="mt-1.5 bg-white border-[#1F4A38]/15 rounded-xl h-11"
                />
              </div>
              <div>
                <Label className="text-[#153326]">Phone *</Label>
                <Input
                  data-testid="booking-phone"
                  value={form.phone}
                  onChange={update("phone")}
                  placeholder="+91 ..."
                  className="mt-1.5 bg-white border-[#1F4A38]/15 rounded-xl h-11"
                />
              </div>
            </div>
            <div>
              <Label className="text-[#153326]">Class Type</Label>
              <Select
                value={form.classType}
                onValueChange={update("classType")}
              >
                <SelectTrigger
                  data-testid="booking-class-type"
                  className="mt-1.5 bg-white border-[#1F4A38]/15 rounded-xl h-11"
                >
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="Group Offline">Group Offline Class</SelectItem>
                  <SelectItem value="Group Online">Group Online Class</SelectItem>
                  <SelectItem value="Private Session">Private Session</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-[#153326]">Message</Label>
              <Textarea
                data-testid="booking-message"
                rows={3}
                value={form.message}
                onChange={update("message")}
                placeholder="Anything you'd like us to know?"
                className="mt-1.5 bg-white border-[#1F4A38]/15 rounded-xl"
              />
            </div>
          </div>

          <button
            type="submit"
            disabled={loading}
            data-testid="booking-submit"
            className="w-full bg-[#D4A24C] hover:bg-[#C29344] disabled:opacity-60 text-[#153326] rounded-full px-6 py-3.5 text-sm font-semibold tracking-wide transition shadow-[0_10px_30px_rgba(212,162,76,0.35)]"
          >
            {loading ? "Sending…" : "Request a Call Back"}
          </button>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default BookingDialog;
